import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { t } from '@aquabox/shared';

/**
 * Yıkıcı işlem onayı. Görünümü `screens/Customers.tsx` içindeki pasifleştirme diyaloğuyla aynıdır.
 *
 * `requireReason` verilirse gerekçe kutusu çıkar; gerekçe `minLength` kadar dolmadan onay
 * düğmesi pasif kalır, `maxLength` aşılamaz ve `onConfirm` gerekçeyi (kırpılmış) alır.
 * Açılınca odak VAZGEÇ düğmesindedir — Enter ile yanlışlıkla onaylanmaz. Esc = `onCancel`;
 * işlem sürerken (`pending`) Esc de kapatmaz.
 */
export interface ConfirmDialogProps {
  title: string;
  body: ReactNode;
  confirmLabel: string;
  tone?: 'danger' | 'default';
  onConfirm: (reason?: string) => void;
  onCancel: () => void;
  pending?: boolean;
  error?: string | null;
  requireReason?: { label: string; minLength: number; maxLength?: number };
}

export function ConfirmDialog({
  title,
  body,
  confirmLabel,
  tone = 'default',
  onConfirm,
  onCancel,
  pending = false,
  error = null,
  requireReason,
}: ConfirmDialogProps) {
  const titleId = useId();
  const reasonId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !pending) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, pending]);

  const trimmed = reason.trim();
  const reasonOk = requireReason === undefined || trimmed.length >= requireReason.minLength;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <div
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--r-box)',
          padding: 24,
          maxWidth: 420,
          width: '90%',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <h2 id={titleId} style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
          {title}
        </h2>
        <div style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>{body}</div>
        {requireReason && (
          <label htmlFor={reasonId} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              {requireReason.label}
            </span>
            <textarea
              id={reasonId}
              rows={3}
              maxLength={requireReason.maxLength}
              value={reason}
              disabled={pending}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
        )}
        {error && (
          <p role="alert" style={{ margin: 0, fontSize: 13, color: 'var(--danger)' }}>
            {error}
          </p>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            ref={cancelRef}
            type="button"
            className="btn"
            style={{ flex: 1 }}
            disabled={pending}
            onClick={onCancel}
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            style={
              tone === 'danger'
                ? { flex: 1, background: 'var(--danger)', borderColor: 'var(--danger)' }
                : { flex: 1 }
            }
            disabled={pending || !reasonOk}
            onClick={() => onConfirm(requireReason ? trimmed : undefined)}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
